/**
 * Gift Threshold Manager
 * Automatically adds/removes gift products based on cart total
 * Integrates with StateManager and toast notification system
 */
(() => {
  'use strict';

  // Configuration will come from theme settings
  let config = {
    enabled: false,
    thresholds: [], // Array of {amount: 100, variantId: 123456789, title: "Free Gift"}
    checkOnLoad: true,
    showNotifications: true,
    autoRemoveBelow: true, // Remove gift if cart drops below threshold
    giftPropertyKey: '_auto_gift', // Mark gifts in cart
    debug: false
  };

  let initialized = false;
  let isProcessing = false;
  let currentCart = null;
  let debounceTimer = null;
  let lastGiftCheck = 0;
  const DEBOUNCE_DELAY = 500; // Wait 500ms before checking threshold
  const MIN_CHECK_INTERVAL = 1000; // Don't check more than once per second

  const log = (...args) => config.debug && console.log('[GiftThreshold]', ...args);
  const error = (...args) => console.error('[GiftThreshold]', ...args);

  // =====================================
  // CONFIGURATION
  // =====================================

  /**
   * Initialize with settings from theme customizer
   */
  const init = (settings = {}) => {
    if (initialized) {
      log('Already initialized');
      return;
    }

    // Merge settings
    config = { ...config, ...settings };

    if (!config.enabled) {
      log('Gift threshold system is disabled');
      return;
    }

    if (!config.thresholds || config.thresholds.length === 0) {
      log('No gift thresholds configured');
      return;
    }

    log('Initializing with config:', config);

    // Set up event listeners
    setupEventListeners();

    // Check cart on page load
    if (config.checkOnLoad) {
      setTimeout(() => checkCartThreshold(), 500);
    }

    // Check if we should open cart drawer after reload (from gift addition)
    const shouldOpenCart = sessionStorage.getItem('gift-threshold-open-cart');
    const shouldShowToast = sessionStorage.getItem('gift-threshold-show-toast');

    if (shouldOpenCart === 'true') {
      log('Opening cart drawer after gift addition reload...');
      sessionStorage.removeItem('gift-threshold-open-cart');

      // Open cart drawer
      setTimeout(() => {
        const miniCart = document.querySelector('mini-cart');
        if (miniCart && typeof miniCart.open === 'function') {
          miniCart.open();
          log('✅ Cart drawer opened after reload');
        }
      }, 1000);
    }

    // Show toast notification after reload (if flagged)
    if (shouldShowToast === 'true') {
      const toastTitle = sessionStorage.getItem('gift-threshold-toast-title');
      const toastMessage = sessionStorage.getItem('gift-threshold-toast-message');
      const toastDelay = parseInt(sessionStorage.getItem('gift-threshold-toast-delay') || '1500', 10);

      console.log('[GiftThreshold] Showing post-reload gift notification...');
      console.log('[GiftThreshold] Toast data:', { toastTitle, toastMessage, delay: toastDelay });

      // Clear flags
      sessionStorage.removeItem('gift-threshold-show-toast');
      sessionStorage.removeItem('gift-threshold-toast-title');
      sessionStorage.removeItem('gift-threshold-toast-message');
      sessionStorage.removeItem('gift-threshold-toast-delay');

      // Show toast after configured delay (allows regular cart toast to show first)
      setTimeout(() => {
        console.log('[GiftThreshold] setTimeout fired, calling showNotification...');
        console.log('[GiftThreshold] showNotification function exists?', typeof showNotification);
        showNotification({
          title: toastTitle || '🎁 Free Gift Added!',
          message: toastMessage || 'Your free gift has been added to your cart!',
          type: 'success'
        });
      }, toastDelay);
    }

    initialized = true;
    log('✅ Gift threshold manager initialized');
  };

  // =====================================
  // EVENT LISTENERS
  // =====================================

  const setupEventListeners = () => {
    // Listen for cart updates from various sources
    document.addEventListener('cart:updated', handleCartUpdate);
    document.addEventListener('cart:refresh', handleCartUpdate);

    // Listen for add-to-cart success (from your unified system)
    document.addEventListener('cart:item-added', handleCartUpdate);

    // Listen for quantity changes
    document.addEventListener('cart:quantity-changed', handleCartUpdate);

    // Listen for cart drawer opening (file upload uses this)
    document.addEventListener('cartdrawer:opened', handleCartUpdate);

    // StateManager integration (if available)
    if (window.PitchPrintStateManager) {
      window.PitchPrintStateManager.on('cart:updated', handleCartUpdate);
    }

    // Also check when mini-cart becomes visible (drawer opens)
    const miniCartObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const miniCart = mutation.target;
          if (miniCart.classList.contains('is-visible') || miniCart.classList.contains('active')) {
            log('🔍 Mini-cart became visible, checking threshold...');
            checkCartThreshold();
          }
        }
      });
    });

    // Wait for mini-cart element to exist, then observe it
    const checkForMiniCart = () => {
      const miniCart = document.querySelector('mini-cart');
      if (miniCart) {
        miniCartObserver.observe(miniCart, { attributes: true });
        log('👀 Watching mini-cart for visibility changes');
      } else {
        setTimeout(checkForMiniCart, 500);
      }
    };
    checkForMiniCart();

    // Monitor cart count changes in the header/bubble
    const observeCartCount = () => {
      const cartBubble = document.querySelector('.cart-count-bubble, #cart-icon-bubble, .header__icon--cart .icon__badge');
      if (cartBubble) {
        const countObserver = new MutationObserver(() => {
          log('🛒 Cart count changed in header, checking threshold...');
          checkCartThreshold();
        });
        countObserver.observe(cartBubble, { childList: true, characterData: true, subtree: true });
        log('👀 Watching cart count for changes');
      } else {
        setTimeout(observeCartCount, 500);
      }
    };
    observeCartCount();

    log('Event listeners registered');
  };

  const handleCartUpdate = (event) => {
    log('🔔 Event received:', event.type, 'Detail:', event.detail);

    // Ignore events generated by this system to prevent loops
    if (event.detail && event.detail.source === 'gift-threshold') {
      log('Ignoring self-generated event');
      return;
    }

    log('Cart update detected:', event.type);

    // Check immediately without debouncing
    // The rate limiting inside checkCartThreshold will prevent excessive calls
    checkCartThreshold();
  };

  // =====================================
  // CART FETCHING
  // =====================================

  /**
   * Get current cart state from Shopify
   */
  const fetchCart = async () => {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      const cart = await response.json();
      currentCart = cart;
      return cart;
    } catch (err) {
      error('Error fetching cart:', err);
      return null;
    }
  };

  // =====================================
  // THRESHOLD LOGIC
  // =====================================

  /**
   * Main threshold checking logic
   */
  const checkCartThreshold = async () => {
    // Rate limiting: Don't check too frequently
    const now = Date.now();
    if (now - lastGiftCheck < MIN_CHECK_INTERVAL) {
      log(`Rate limited: Last check was ${now - lastGiftCheck}ms ago, waiting...`);
      return;
    }

    if (isProcessing) {
      log('Already processing, skipping...');
      return;
    }

    isProcessing = true;
    lastGiftCheck = now;

    try {
      const cart = await fetchCart();
      if (!cart) {
        isProcessing = false;
        return;
      }

      // Calculate cart total excluding any auto-added gifts
      let cartTotalExcludingGifts = cart.total_price;
      const giftItems = cart.items.filter(item =>
        item.properties && item.properties[config.giftPropertyKey]
      );

      if (giftItems.length > 0) {
        const giftTotal = giftItems.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
        cartTotalExcludingGifts = cart.total_price - giftTotal;
        log(`Cart total: $${cart.total_price / 100}, Gift items total: $${giftTotal / 100}, Excluding gifts: $${cartTotalExcludingGifts / 100}`);
      } else {
        log('Cart total:', cart.total_price / 100, 'items:', cart.item_count);
      }

      // Sort thresholds by amount (highest first)
      const sortedThresholds = [...config.thresholds].sort((a, b) => b.amount - a.amount);

      // Find the highest threshold met (using total excluding gifts)
      const metThreshold = sortedThresholds.find(threshold => {
        const thresholdCents = threshold.amount * 100; // Convert to cents
        return cartTotalExcludingGifts >= thresholdCents;
      });

      if (metThreshold) {
        await handleThresholdMet(cart, metThreshold);
      } else {
        await handleBelowThreshold(cart);
      }

    } catch (err) {
      error('Error checking threshold:', err);
    } finally {
      isProcessing = false;
    }
  };

  /**
   * Handle when threshold is met
   */
  const handleThresholdMet = async (cart, threshold) => {
    log(`✅ Threshold met: $${threshold.amount} - Gift: ${threshold.title}`);

    // Check if gift already in cart
    const giftInCart = cart.items.find(item =>
      item.variant_id === threshold.variantId ||
      (item.properties && item.properties[config.giftPropertyKey] === String(threshold.variantId))
    );

    if (giftInCart) {
      log('Gift already in cart:', giftInCart.title);

      // Remove other tier gifts if they exist
      await removeOtherTierGifts(cart, threshold.variantId);
      return;
    }

    // Add gift to cart
    await addGiftToCart(threshold);
  };

  /**
   * Handle when cart is below all thresholds
   */
  const handleBelowThreshold = async (cart) => {
    if (!config.autoRemoveBelow) {
      return;
    }

    log('Cart below all thresholds');

    // Find all gift items in cart
    const giftItems = cart.items.filter(item =>
      item.properties && item.properties[config.giftPropertyKey]
    );

    if (giftItems.length > 0) {
      log(`Removing ${giftItems.length} gift items`);
      // Note: removeFromCart() will handle notification and page reload
      // Find the line item key (1-based index) for each gift
      for (const giftItem of giftItems) {
        const lineIndex = cart.items.findIndex(item => item.key === giftItem.key);
        if (lineIndex !== -1) {
          await removeFromCart(lineIndex + 1); // Shopify uses 1-based index
        }
      }
    }
  };

  /**
   * Remove gifts from lower tiers when higher tier is achieved
   */
  const removeOtherTierGifts = async (cart, currentGiftVariantId) => {
    const otherGifts = cart.items.filter(item => {
      if (!item.properties || !item.properties[config.giftPropertyKey]) {
        return false;
      }
      return item.variant_id !== currentGiftVariantId;
    });

    if (otherGifts.length > 0) {
      log(`Removing ${otherGifts.length} lower-tier gifts`);
      // Find the line item key (1-based index) for each gift
      for (const gift of otherGifts) {
        const lineIndex = cart.items.findIndex(item => item.key === gift.key);
        if (lineIndex !== -1) {
          await removeFromCart(lineIndex + 1); // Shopify uses 1-based index
        }
      }
    }
  };

  // =====================================
  // CART OPERATIONS
  // =====================================

  /**
   * Add gift product to cart
   */
  const addGiftToCart = async (threshold) => {
    try {
      log(`Adding gift to cart: ${threshold.title} (variant: ${threshold.variantId})`);

      const formData = new FormData();
      formData.append('items[0][id]', threshold.variantId);
      formData.append('items[0][quantity]', '1');
      formData.append(`items[0][properties][${config.giftPropertyKey}]`, String(threshold.variantId));
      formData.append('items[0][properties][Gift]', 'Free Gift');

      // Add sections parameter for cart drawer refresh
      formData.append('sections', 'mini-cart,cart-icon-bubble,mobile-cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      // Add custom message if configured
      if (threshold.customMessage) {
        formData.append('items[0][properties][_gift_message]', threshold.customMessage);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add gift: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      log('✅ Gift added successfully:', result);

      // Set flags for post-reload notification and cart drawer opening
      sessionStorage.setItem('gift-threshold-open-cart', 'true');

      if (config.showNotifications) {
        sessionStorage.setItem('gift-threshold-show-toast', 'true');
        sessionStorage.setItem('gift-threshold-toast-title', '🎁 Free Gift Added!');
        sessionStorage.setItem('gift-threshold-toast-message', threshold.notificationMessage || `${threshold.title} has been added to your cart`);

        // Set delay for gift toast to show after regular cart toast finishes
        // Regular toast shows at ~1.5s and lasts 5s, so show gift toast at 7s (1.5 + 5 + 0.5 buffer)
        sessionStorage.setItem('gift-threshold-toast-delay', '7000'); // Show 7 seconds after page load
      }

      // Reload page to show gift (matches PitchPrint's proven approach)
      // This is the most reliable way to ensure cart drawer displays correctly
      log('🔄 Reloading page to display gift in cart...');
      setTimeout(() => {
        window.location.reload();
      }, 500);

    } catch (err) {
      error('Error adding gift to cart:', err);
    }
  };

  /**
   * Remove item from cart by line item key
   */
  const removeFromCart = async (lineItemKey) => {
    try {
      log(`Removing item from cart: ${lineItemKey}`);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: lineItemKey,
          quantity: 0
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to remove item: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      log('✅ Item removed successfully');

      // Set flags for post-reload notification
      if (config.showNotifications) {
        sessionStorage.setItem('gift-threshold-show-toast', 'true');
        sessionStorage.setItem('gift-threshold-toast-title', 'Gift Removed');
        sessionStorage.setItem('gift-threshold-toast-message', 'Almost there! Spend $300 to unlock your free gift again.');

        // Gift removal doesn't have a regular cart toast, so show immediately
        sessionStorage.setItem('gift-threshold-toast-delay', '1500');
      }

      // Reload page to show updated cart after gift removal
      log('🔄 Reloading page after gift removal...');
      setTimeout(() => {
        window.location.reload();
      }, 500);

    } catch (err) {
      error('Error removing from cart:', err);
    }
  };

  // =====================================
  // UI UPDATES
  // =====================================

  /**
   * Show notification using existing toast system or fallback
   */
  const showNotification = (options) => {
    const { title, message, type = 'success' } = options;

    // Try using StateManager to show toast (preferred method)
    if (window.PitchPrintStateManager && typeof window.PitchPrintStateManager.emit === 'function') {
      log('Using StateManager to trigger toast');

      window.PitchPrintStateManager.emit('toast:activate', {
        messageType: 'addToCartSuccess',
        customTitle: title,
        customMessage: message
      });

      log(`📢 Toast activated via StateManager: ${title} - ${message}`);
      return;
    }

    // Fallback: Try direct toast function
    if (window.TOAST_CONFIG && typeof window.showToastMessage === 'function') {
      log('Using existing toast system');

      window.showToastMessage({
        messageType: 'addToCartSuccess',
        customMessage: message,
        customTitle: title
      });

      log(`📢 Toast shown: ${title} - ${message}`);
      return;
    }

    // Last resort: dispatch custom event
    log('Toast system not available, dispatching custom event');
    document.dispatchEvent(new CustomEvent('gift:notification', {
      detail: { title, message, type }
    }));

    log(`📢 Notification event dispatched: ${title} - ${message}`);
  };

  // =====================================
  // PUBLIC API
  // =====================================

  window.GiftThresholdManager = {
    init,
    checkCartThreshold,
    fetchCart,
    getConfig: () => ({ ...config }),
    isInitialized: () => initialized,
    updateConfig: (newSettings) => {
      config = { ...config, ...newSettings };
      log('Config updated:', config);
    },
    enable: () => {
      config.enabled = true;
      log('Enabled');
      checkCartThreshold();
    },
    disable: () => {
      config.enabled = false;
      log('Disabled');
    },
    // Manual trigger for testing
    manualCheck: () => {
      log('🔧 Manual threshold check triggered');
      // Reset rate limiting to allow immediate check
      lastGiftCheck = 0;
      isProcessing = false;
      return checkCartThreshold();
    }
  };

  log('Gift Threshold Manager loaded (call init() to activate)');
})();
